import React, { useContext, useEffect } from 'react';
import { QuestionsRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import {
  arrayOf as arrayType,
  extend,
  params,
  QueryContext,
  shape as shapeType,
  string as stringType,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import {
  FeatureBasedRecommendationsImporterVite as FeatureBasedRecommendationsImporter
} from '@thd-olt-component-react/feature-based-recommendations';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { MediaGalleryCarousel, MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { PprMetadata } from '@thd-olt-component-react/metadata';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { useLocation, useParams } from '@thd-olt-component-react/router';
import { SalientPoints } from '@thd-olt-component-react/salient-points';
import { DynamicRecsWrapper } from '@thd-olt-component-react/thd-recs-containers';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton } from '@thd-olt-component-react/thd-sharebelt';

import ProductDetailsQuestionsRatings from '../components/ProductDetailsQuestionsRatings';
import ProductQuestionsHeader from '../components/ProductQuestionsHeader';
import QandAImporter from '../components/QandAImporter';
import ViewDetailsLink from '../components/ViewDetailsLink';

const RECS_REQUEST_KEY = 'aGAQFG4j6QtVTSWqujfFYWeIU6BR5Mee';
const VIEW_DETAILS_TRACKING_PARAM = '?emt=ppq_vfpd';

export const ProductQandA = () => {
  declareContexts([ExperienceContext, QueryContext]);
  const qctx = useContext(QueryContext);
  const { channel } = useContext(ExperienceContext);
  const { itemId, page } = useParams();
  const { pathname, search } = useLocation();
  const emtLinksPath = page ? pathname : `${pathname}/1`;

  const isMobile = channel === 'mobile';
  const pageNumber = page === undefined ? '1' : page;
  const seoPageNumber = parseInt(pageNumber, 10);

  const { data, loading } = useDataModel('product', { variables: { itemId } });
  const { product } = data || {};

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (data && !loading) {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
          'paginated-product-reviews.page-load',
          { product, pageType: 'questions' }
        );
      }
    }
  }, []);

  return (
    <div className="sui-bg-primary">
      <div className="sui-max-w-screen-2xl sui-m-auto">
        <ErrorBoundary name="redirector">
          <QuestionsRedirector
            pathWithQueryParams={pathname + search}
            itemId={itemId}
          />
        </ErrorBoundary>

        <ErrorBoundary name="metadata">
          <PprMetadata itemId={itemId} page={page} ppq />
        </ErrorBoundary>

        <div className="sui-px-3 2xl:sui-px-0 sui-mt-2">
          <ErrorBoundary name="breadcrumbs">
            <Breadcrumbs itemId={itemId} />
          </ErrorBoundary>
        </div>

        {isMobile && (
          <ErrorBoundary name="product-questions-header">
            <div className="sui-px-3 2xl:sui-px-0 sui-mt-6 sui-mb-4">
              <ProductQuestionsHeader itemId={itemId} />
            </div>
          </ErrorBoundary>
        )}

        {!isMobile && (
          <ErrorBoundary name="product-info-bar">
            <div className="sui-px-3 2xl:sui-px-0 sui-mt-6">
              <ProductInfoBar itemId={itemId} />
            </div>
          </ErrorBoundary>
        )}

        <div className="sui-grid sui-grid-cols-1 lg:sui-grid-cols-12 sui-w-full">
          <div className="sui-col-span-6 sui-px-3 2xl:sui-px-0">
            {isMobile && (
              <ErrorBoundary name="product-details">
                <ProductDetails itemId={itemId}>
                  <ProductDetails.BrandCollection />
                  <ProductDetailsQuestionsRatings itemId={itemId} page={page} />
                </ProductDetails>
              </ErrorBoundary>
            )}

            {!isMobile && (
              <ErrorBoundary name="media-gallery">
                <MediaGalleryDesktop itemId={itemId} mainImageFetchPriority="high" />
              </ErrorBoundary>
            )}

            {!isMobile && (
              <ErrorBoundary name="sharebelt-desktop">
                <Sharebelt horizontal itemId={itemId}>
                  <ShareBeltShareButton email />
                  <ShareBeltPrint />
                </Sharebelt>
              </ErrorBoundary>
            )}
          </div>

          {!isMobile && (
            <div className="lg:sui-col-start-8 lg:sui-col-span-4 sui-px-3 lg:sui-px-0">
              <ErrorBoundary name="product-questions-header">
                <ProductQuestionsHeader itemId={itemId} />
              </ErrorBoundary>
              <ErrorBoundary name="product-details">
                <ProductDetails itemId={itemId}>
                  <ProductDetails.BrandCollection />
                  <ProductDetailsQuestionsRatings itemId={itemId} page={page} />
                </ProductDetails>
              </ErrorBoundary>
              <ErrorBoundary name="salient-points">
                <SalientPoints itemId={itemId} max={3} />
              </ErrorBoundary>
              <div className="sui-mt-3">
                <ViewDetailsLink itemId={itemId} trackingParam={VIEW_DETAILS_TRACKING_PARAM} />
              </div>
            </div>
          )}

          {isMobile && (
            <>
              <ErrorBoundary name="media-gallery">
                <div className="sui-col-span-12">
                  <MediaGalleryCarousel itemId={itemId} firstItemFetchPriority="high" />
                </div>
              </ErrorBoundary>

              <ErrorBoundary name="product-info-bar">
                <div className="sui-col-span-12 sui-px-3 2xl:sui-px-0">
                  <ProductInfoBar itemId={itemId} />
                </div>
              </ErrorBoundary>
            </>
          )}
        </div>

        {isMobile && (
          <div className="sui-px-3 2xl:sui-px-0">
            <div className="sui-my-3">
              <ViewDetailsLink itemId={itemId} trackingParam={VIEW_DETAILS_TRACKING_PARAM} />
            </div>
          </div>
        )}

        <ErrorBoundary name="questions-and-answers">
          <QandAImporter
            itemId={itemId}
            seoPageNumber={seoPageNumber}
            enableExperienceProperty
            hydrator={{
              className: 'sui-px-3 2xl:sui-px-0 sui-mb-4 empty:sui-m-0 empty:sui-p-0',
            }}
            dynamic={{
              pageType: 'productQandA'
            }}
          />
        </ErrorBoundary>

        <ErrorBoundary name="feature-based-recommendations">
          <FeatureBasedRecommendationsImporter
            hideOnMobile
            itemId={itemId}
            qctx={qctx}
            hydrator={{
              className: 'sui-mb-4 empty:sui-mb-0',
              scrollBuffer: 750,
              id: 'fbr-hydrator'
            }}
            dynamic={{
              pageType: 'productQandA'
            }}
          />
        </ErrorBoundary>

        <ErrorBoundary name="dynamic-recs">
          <DynamicComponent pageType="productQandA">
            <DynamicRecsWrapper
              anchorId={itemId}
              errorBoundary
              hideATC
              hydrator={{ id: 'dynamic-recs-hydrator', scrollBuffer: 750 }}
              impressionData={{
                component: 'DynamicRecs',
                type: 'product'
              }}
              requestKey={RECS_REQUEST_KEY}
              schemaName="pip_alternatives"
            />
          </DynamicComponent>
        </ErrorBoundary>

        <ErrorBoundary name="emt-links">
          <EmtLinks
            dynamic={{ pageType: 'productQandA' }}
            hydrator={{ scrollBuffer: 750, className: 'sui-px-3 2xl:sui-px-0 sui-mt-4 empty:sui-mt-0' }}
            path={emtLinksPath}
          />
        </ErrorBoundary>
      </div>
    </div>
  );
};

ProductQandA.dataModel = extend(
  {
    product: params({ itemId: stringType().isRequired() }).shape({
      itemId: stringType(),
      dataSources: stringType(),
      identifiers: shapeType({
        brandName: stringType(),
        canonicalUrl: stringType(),
        itemId: stringType(),
        modelNumber: stringType(),
        omsThdSku: stringType(),
        productLabel: stringType(),
        storeSkuNumber: stringType(),
        specialOrderSku: stringType(),
        upcGtin13: stringType()
      }),
      media: shapeType({
        image: shapeType({
          url: stringType()
        }).client(),
        images: arrayType(shapeType({
          url: stringType()
        }))
      }),
      taxonomy: shapeType({
        breadCrumbs: arrayType(shapeType({
          label: stringType(),
          url: stringType()
        }))
      })
    })
  },
  Breadcrumbs,
  DynamicRecsWrapper,
  EmtLinks,
  FeatureBasedRecommendationsImporter,
  MediaGalleryCarousel,
  MediaGalleryDesktop,
  ProductDetails,
  ProductDetailsQuestionsRatings,
  ProductQuestionsHeader,
  QandAImporter,
  SalientPoints,
  QuestionsRedirector,
  ViewDetailsLink,
  PprMetadata
);